<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedOption"
      searchable
      class="tw-min-w-[150px]"
      data-test="tc-filter-origin"
      :label="meta.filter.quickFilter ? '' : $t('Origin')"
      :list="originOptions"
      :placeholder="$t('All')"
      :loading="loading"
      can-deselect
      labels-primary
    />
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack } from '@worksome/wds-components'
import { computed, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import { graphql } from '@/gql'
import { TrustedContactOrigin } from '@/gql/graphql'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

defineProps<{ meta: FilterMetaDataProp }>()

const model: ModelRef<TrustedContactOrigin | null> = defineModel({
  default: () => null,
})

const TrustedContactsOriginQuery = graphql(`
  query TrustedContactsOriginFilterQuery($accounts: [ID!], $first: Int!, $page: Int!) {
    trustedContacts(accounts: $accounts, first: $first, page: $page) {
      data {
        id
        origin
      }
    }
  }
`)

const { result, loading } = useQuery(
  TrustedContactsOriginQuery as any,
  { accounts: [currentAccount.globalId], first: PER_PAGE_MAXIMUM, page: 1 },
  { clientId: 'publicApi' },
)

const originLabels: Record<TrustedContactOrigin, string> = {
  [TrustedContactOrigin.Invited]: $t('Invited'),
  [TrustedContactOrigin.Added]: $t('Added'),
}

const originOptions = computed(() => {
  const trustedContacts = result.value?.trustedContacts?.data ?? []

  const uniqueOrigins = new Set<TrustedContactOrigin>()
  trustedContacts.forEach((contact: { origin: TrustedContactOrigin }) => {
    if (contact.origin) {
      uniqueOrigins.add(contact.origin)
    }
  })

  return Array.from(uniqueOrigins)
    .map((origin) => ({
      label: originLabels[origin] || origin,
      value: origin,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const selectedOption = computed({
  get: () => {
    if (!model.value) return null
    return originOptions.value.find((o) => o.value === model.value) ?? null
  },
  set: (option) => {
    model.value = (option?.value as TrustedContactOrigin) ?? null
  },
})
</script>
